"use client";

import { formatDate } from "@/lib/format";
import { switchFiscalYearAction } from "@/app/fiscal-year-actions";
import { Icon } from "./Icon";

export type FiscalYearOption = {
  id: number;
  term: number | null;
  startDate: Date;
  endDate: Date;
  isClosed: boolean;
};

type Props = {
  /** 現在表示中の会計期間（過去の期を選んでいればその期、未選択なら当期）。 */
  selected: FiscalYearOption | null;
  /** 実際の当期（未締めの最新期）。selectedと異なる場合は「過去を表示中」の警告表示に使う。 */
  current: FiscalYearOption | null;
  /** 選択肢一覧（新しい順）。 */
  allFiscalYears: FiscalYearOption[];
};

/** freeeスタイルの会計期間切替。過去の期を選ぶとレポート・仕訳帳・元帳がその期のデータに切り替わる。 */
export function FiscalYearSwitcher({ selected, current, allFiscalYears }: Props) {
  if (!selected) return null;
  const isOverride = current != null && selected.id !== current.id;

  return (
    <details className="relative">
      <summary
        className="flex cursor-pointer list-none items-center gap-1.5 rounded px-2 py-0.5 text-[12px] font-medium"
        style={{
          background: isOverride ? "color-mix(in srgb, var(--warn) 15%, transparent)" : "transparent",
          color: isOverride ? "var(--warn)" : "var(--text-muted)",
          border: isOverride ? "1px solid var(--warn)" : "1px solid transparent",
        }}
      >
        <Icon name="closing" className="!h-4 !w-4" />
        {formatDate(selected.startDate)} 〜 {formatDate(selected.endDate)}
        {isOverride && <span>（過去の期間を表示中）</span>}
      </summary>

      <div
        className="absolute right-0 z-30 mt-1 max-h-80 w-72 overflow-auto rounded-[var(--radius)] border"
        style={{ background: "var(--surface)", borderColor: "var(--border)", boxShadow: "var(--shadow-md)" }}
      >
        {isOverride && (
          <form action={switchFiscalYearAction}>
            <input type="hidden" name="fiscalYearId" value="" />
            <button
              type="submit"
              className="w-full border-b px-3 py-2 text-left text-[12px] font-semibold"
              style={{ borderColor: "var(--border)", color: "var(--accent-strong)" }}
            >
              ← 当期に戻る
            </button>
          </form>
        )}
        {allFiscalYears.map((fy) => {
          const isActive = fy.id === selected.id;
          return (
            <form action={switchFiscalYearAction} key={fy.id}>
              <input type="hidden" name="fiscalYearId" value={fy.id} />
              <button
                type="submit"
                disabled={isActive}
                className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-[12px]"
                style={{
                  background: isActive ? "var(--accent-soft)" : "transparent",
                  color: isActive ? "var(--accent-strong)" : "var(--text)",
                  cursor: isActive ? "default" : "pointer",
                }}
              >
                <span>
                  {fy.term != null ? `第${fy.term}期 ` : ""}
                  {formatDate(fy.startDate)}〜{formatDate(fy.endDate)}
                </span>
                {fy.isClosed && (
                  <span
                    className="shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium"
                    style={{ background: "var(--surface-2)", color: "var(--text-muted)" }}
                  >
                    締め済み
                  </span>
                )}
              </button>
            </form>
          );
        })}
      </div>
    </details>
  );
}
